import { useEffect, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { LocalFirstFooter } from '@life-os/ui';
import { useRegistry, useStore } from './store.js';
import { useRouter } from './router.js';
import { CommandPalette } from './CommandPalette.js';
import { Sidebar } from './Sidebar.js';
import { RouteIcon } from './icons.js';
import { useAsync } from '../lib/useAsync.js';

function SearchIcon() {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={16} height={16} aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" fill="none" />
      <path d="m21 21-4.3-4.3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { getRuntimeStatus, revision } = useStore();
  const registry = useRegistry();
  const { path, navigate } = useRouter();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const runtime = useAsync(() => getRuntimeStatus(), [revision]);

  const current = registry.all().find((s) => s.surface.kind === 'route' && s.surface.path === path);
  const contextLabel = current?.label ?? 'Life OS';

  const onDrawerClick = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('a')) setDrawerOpen(false);
  };

  return (
    <div className="app">
      <Sidebar onOpenPalette={() => setPaletteOpen(true)} />

      {/* Desktop top app bar */}
      <header className="app__topbar">
        <div className="app__topctx">
          <span className="app__topctx-icon">
            <RouteIcon route={path} size={17} />
          </span>
          <span className="app__topctx-label">{contextLabel}</span>
        </div>
        <div className="app__topcmd">
          <button className="app__search" onClick={() => setPaletteOpen(true)}>
            <SearchIcon />
            Search or jump to anything
            <kbd>⌘K</kbd>
          </button>
        </div>
        <div className="app__topactions">
          <button className="lc-btn" data-variant="primary" onClick={() => navigate('/capture')}>
            <svg className="icon" viewBox="0 0 24 24" width={16} height={16} aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" />
            </svg>
            Capture
          </button>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className="app__mobilebar">
        <button className="app__iconbtn" aria-label="Open menu" onClick={() => setDrawerOpen(true)}>
          <svg className="icon" viewBox="0 0 24 24" width={20} height={20} aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
          </svg>
        </button>
        <div className="app__mobilebrand">
          <span className="app__brand-mark" style={{ width: 24, height: 24 }}>
            <svg viewBox="0 0 24 24" width={14} height={14} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3a9 9 0 1 0 9 9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </span>
          {contextLabel}
        </div>
        <button className="app__iconbtn" aria-label="Search" onClick={() => setPaletteOpen(true)}>
          <SearchIcon />
        </button>
      </header>

      <main className="app__main">{children}</main>

      <div className="app__footer">
        {runtime.value ? (
          <LocalFirstFooter status={runtime.value} />
        ) : (
          <div className="lc-footer">Loading runtime…</div>
        )}
      </div>

      {/* Mobile drawer */}
      {drawerOpen ? (
        <div className="app__drawer-scrim" onClick={() => setDrawerOpen(false)} role="presentation">
          <div className="app__drawer" onClick={onDrawerClick}>
            <Sidebar onOpenPalette={() => { setDrawerOpen(false); setPaletteOpen(true); }} />
          </div>
        </div>
      ) : null}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
